import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Globe, 
  Search,
  Lock,
  ArrowRight,
  Plus,
  Upload,
  Download,
  Pencil,
  Trash2,
  X,
  Check,
  FileSpreadsheet,
  Loader2
} from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TerminologyPage = () => {
  const { isAdmin, token } = useAuth();
  const [terms, setTerms] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  
  // Admin states
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editingTerm, setEditingTerm] = useState(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const fileInputRef = useRef(null);
  
  // Form state
  const [formData, setFormData] = useState({
    english: "",
    french: "",
    category: "alerts"
  });

  const categoryOptions = [
    { id: "alerts", name: "Alerts" },
    { id: "precipitation", name: "Precipitation" },
    { id: "wind", name: "Wind" },
    { id: "temperature", name: "Temperature" },
    { id: "clouds", name: "Clouds" },
    { id: "visibility", name: "Visibility" },
    { id: "pressure", name: "Pressure" },
    { id: "storms", name: "Storms" },
    { id: "measurements", name: "Measurements" },
    { id: "time", name: "Time" },
    { id: "geography", name: "Geography" },
    { id: "advisory", name: "Advisory" },
    { id: "organization", name: "Organization" },
  ];

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchTerms();
  }, [selectedCategory, searchQuery]);

  const fetchData = async () => {
    try {
      const [termsRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/terminology`),
        axios.get(`${API}/terminology/categories`)
      ]);
      setTerms(termsRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      console.error("Failed to fetch terminology:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTerms = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedCategory) params.append("category", selectedCategory);
      if (searchQuery) params.append("search", searchQuery);
      
      const response = await axios.get(`${API}/terminology?${params.toString()}`);
      setTerms(response.data);
    } catch (error) {
      console.error("Failed to fetch terms:", error);
    }
  };

  const handleAddTerm = async () => {
    if (!formData.english.trim() || !formData.french.trim()) {
      toast.error("Both English and French terms are required");
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/terminology`, {
        english: formData.english.trim(),
        french: formData.french.trim(),
        category: formData.category,
        locked: true
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Term added successfully");
      setAddDialogOpen(false);
      setFormData({ english: "", french: "", category: "alerts" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add term");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateTerm = async () => {
    if (!formData.english.trim() || !formData.french.trim()) {
      toast.error("Both English and French terms are required");
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.put(`${API}/terminology/${editingTerm.id}`, {
        english: formData.english.trim(),
        french: formData.french.trim(),
        category: formData.category,
        locked: true
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Term updated successfully");
      setEditingTerm(null);
      setFormData({ english: "", french: "", category: "alerts" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update term");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteTerm = async (termId) => {
    if (!confirm("Are you sure you want to delete this term?")) return;

    try {
      await axios.delete(`${API}/terminology/${termId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Term deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete term");
    }
  };

  const handleExportCSV = () => {
    const csvContent = [
      ["ID", "English", "French", "Category", "Locked"].join(","),
      ...terms.map(t => [
        t.id,
        `"${t.en_ca.replace(/"/g, '""')}"`,
        `"${t.fr_ca.replace(/"/g, '""')}"`,
        t.category,
        t.locked
      ].join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `terminology_export_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success("Terminology exported to CSV");
  };

  const handleImportCSV = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsImporting(true);
    const reader = new FileReader();
    
    reader.onload = async (e) => {
      try {
        const text = e.target?.result;
        const lines = text.split("\n").filter(line => line.trim());
        const header = lines[0].toLowerCase();
        
        // Check for required columns
        if (!header.includes("english") || !header.includes("french")) {
          toast.error("CSV must have 'english' and 'french' columns");
          setIsImporting(false);
          return;
        }

        const headerCols = lines[0].split(",").map(h => h.trim().toLowerCase());
        const englishIdx = headerCols.findIndex(h => h.includes("english"));
        const frenchIdx = headerCols.findIndex(h => h.includes("french"));
        const categoryIdx = headerCols.findIndex(h => h.includes("category"));

        const termsToImport = [];
        for (let i = 1; i < lines.length; i++) {
          const cols = parseCSVLine(lines[i]);
          if (cols[englishIdx] && cols[frenchIdx]) {
            termsToImport.push({
              english: cols[englishIdx].trim(),
              french: cols[frenchIdx].trim(),
              category: categoryIdx >= 0 ? cols[categoryIdx]?.trim() || "alerts" : "alerts",
              locked: true
            });
          }
        }

        if (termsToImport.length === 0) {
          toast.error("No valid terms found in CSV");
          setIsImporting(false);
          return;
        }

        // Bulk import
        const response = await axios.post(`${API}/terminology/bulk`, {
          terms: termsToImport
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });

        toast.success(`Imported ${response.data.imported} terms (${response.data.skipped} skipped)`);
        setImportDialogOpen(false);
        fetchData();
      } catch (error) {
        toast.error(error.response?.data?.detail || "Failed to import CSV");
      } finally {
        setIsImporting(false);
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
    };

    reader.readAsText(file);
  };

  // Helper to parse CSV line handling quoted values
  const parseCSVLine = (line) => {
    const result = [];
    let current = "";
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = "";
      } else {
        current += char;
      }
    }
    result.push(current.trim());
    return result;
  };

  const openEditDialog = (term) => {
    setEditingTerm(term);
    setFormData({
      english: term.en_ca,
      french: term.fr_ca,
      category: term.category
    });
  };

  const getCategoryColor = (categoryId) => {
    const colors = {
      alerts: "bg-red-500/10 text-red-600 border-red-500/30",
      precipitation: "bg-blue-500/10 text-blue-600 border-blue-500/30",
      wind: "bg-cyan-500/10 text-cyan-600 border-cyan-500/30",
      temperature: "bg-orange-500/10 text-orange-600 border-orange-500/30",
      clouds: "bg-gray-500/10 text-gray-600 border-gray-500/30",
      visibility: "bg-purple-500/10 text-purple-600 border-purple-500/30",
      pressure: "bg-emerald-500/10 text-emerald-600 border-emerald-500/30",
      storms: "bg-rose-500/10 text-rose-600 border-rose-500/30",
      measurements: "bg-indigo-500/10 text-indigo-600 border-indigo-500/30",
      time: "bg-yellow-500/10 text-yellow-600 border-yellow-500/30",
      geography: "bg-teal-500/10 text-teal-600 border-teal-500/30",
      advisory: "bg-primary/10 text-primary border-primary/30",
      organization: "bg-slate-500/10 text-slate-600 border-slate-500/30",
    };
    return colors[categoryId] || "bg-muted text-muted-foreground border-border";
  };

  return (
    <div className="min-h-screen pb-12">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-border">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <Globe className="w-6 h-6 text-primary" />
            <span className="font-heading text-xl font-bold tracking-tight text-foreground">MeteoTranslate</span>
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <Link to="/translate" className="nav-link text-sm font-medium" data-testid="nav-translate">
              Translate
            </Link>
            <Link to="/terminology" className="nav-link active text-sm font-medium" data-testid="nav-terminology">
              Terminology
            </Link>
            <Link to="/metrics" className="nav-link text-sm font-medium" data-testid="nav-metrics">
              Metrics
            </Link>
            <Link to="/analytics" className="nav-link text-sm font-medium" data-testid="nav-analytics">
              Analytics
            </Link>
            <Link to="/history" className="nav-link text-sm font-medium" data-testid="nav-history">
              History
            </Link>
            <Link to="/settings" className="nav-link text-sm font-medium" data-testid="nav-settings">
              Settings
            </Link>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 pt-8">
        {/* Page Header */}
        <div className="flex items-start justify-between mb-8">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Lock className="w-5 h-5 text-primary" />
              <h1 className="font-heading text-3xl font-bold tracking-tight" data-testid="page-title">
                Terminology Dictionary
              </h1>
            </div>
            <p className="text-muted-foreground">
              {terms.length} locked meteorological terms with guaranteed translation accuracy
            </p>
          </div>

          {/* Admin Actions */}
          {isAdmin && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportCSV}
                className="border-border"
                data-testid="export-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setImportDialogOpen(true)}
                className="border-border"
                data-testid="import-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                Import CSV
              </Button>
              <Button
                size="sm"
                onClick={() => setAddDialogOpen(true)}
                data-testid="add-term-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Term
              </Button>
            </div>
          )}
        </div>

        {/* Search & Filters */}
        <div className="flex flex-col lg:flex-row gap-6 mb-8">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search terms in English or French..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-12 bg-card border-border"
              data-testid="search-input"
            />
          </div>

          {/* Category Filters */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setSelectedCategory(null)}
              className={`category-pill ${!selectedCategory ? "active" : ""}`}
              data-testid="category-all"
            >
              All ({categories.reduce((sum, c) => sum + c.count, 0)})
            </button>
            {categories.slice(0, 6).map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`category-pill ${selectedCategory === category.id ? "active" : ""}`}
                data-testid={`category-${category.id}`}
              >
                {category.name} ({category.count})
              </button>
            ))}
          </div>
        </div>

        {/* Terms Table */}
        <div className="bg-white rounded-lg border border-border overflow-hidden shadow-card">
          <ScrollArea className="h-[600px]">
            <table className="data-table">
              <thead className="sticky top-0">
                <tr>
                  <th className="w-24">ID</th>
                  <th>English (EN-CA)</th>
                  <th className="w-12 text-center">
                    <ArrowRight className="w-4 h-4 inline" />
                  </th>
                  <th>French (FR-CA)</th>
                  <th className="w-32">Category</th>
                  {isAdmin && <th className="w-24">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  Array.from({ length: 10 }).map((_, i) => (
                    <tr key={i}>
                      <td><div className="skeleton h-4 w-16 rounded" /></td>
                      <td><div className="skeleton h-4 w-40 rounded" /></td>
                      <td></td>
                      <td><div className="skeleton h-4 w-40 rounded" /></td>
                      <td><div className="skeleton h-4 w-20 rounded" /></td>
                      {isAdmin && <td></td>}
                    </tr>
                  ))
                ) : terms.length === 0 ? (
                  <tr>
                    <td colSpan={isAdmin ? 6 : 5} className="text-center py-12 text-muted-foreground">
                      No terms found matching your search
                    </td>
                  </tr>
                ) : (
                  terms.map((term, i) => (
                    <tr key={term.id} className="table-row-hover" data-testid={`term-row-${i}`}>
                      <td>
                        <span className="font-mono text-xs text-muted-foreground">
                          {term.id}
                        </span>
                      </td>
                      <td className="font-medium">{term.en_ca}</td>
                      <td className="text-center">
                        <Lock className="w-3 h-3 text-primary inline" />
                      </td>
                      <td className="font-medium">{term.fr_ca}</td>
                      <td>
                        <span className={`inline-flex items-center px-2 py-1 rounded text-xs border ${getCategoryColor(term.category)}`}>
                          {term.category}
                        </span>
                      </td>
                      {isAdmin && (
                        <td>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => openEditDialog(term)}
                              className="p-1.5 hover:bg-gray-100 rounded"
                              title="Edit"
                              data-testid={`edit-term-${i}`}
                            >
                              <Pencil className="w-3.5 h-3.5 text-gray-500" />
                            </button>
                            <button
                              onClick={() => handleDeleteTerm(term.id)}
                              className="p-1.5 hover:bg-red-50 rounded"
                              title="Delete"
                              data-testid={`delete-term-${i}`}
                            >
                              <Trash2 className="w-3.5 h-3.5 text-red-500" />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </ScrollArea>
        </div>

        {/* Info Box */}
        <div className="mt-8 p-6 bg-primary/5 border border-primary/20 rounded-lg">
          <div className="flex items-start gap-4">
            <Lock className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-heading text-lg font-bold mb-2">Terminology Locking</h3>
              <p className="text-sm text-muted-foreground">
                These terms are "locked" in our translation system, meaning they will always be translated 
                using these exact pairs regardless of the AI model used. This ensures 100% accuracy for 
                critical meteorological terminology as required by ECCC standards.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Add Term Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5" />
              Add New Term
            </DialogTitle>
            <DialogDescription>
              Add a new locked terminology pair for translation
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-1.5 block">English (EN-CA)</label>
              <Input
                value={formData.english}
                onChange={(e) => setFormData({ ...formData, english: e.target.value })}
                placeholder="Enter English term..."
                data-testid="add-english-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">French (FR-CA)</label>
              <Input
                value={formData.french}
                onChange={(e) => setFormData({ ...formData, french: e.target.value })}
                placeholder="Enter French translation..."
                data-testid="add-french-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Category</label>
              <Select
                value={formData.category}
                onValueChange={(value) => setFormData({ ...formData, category: value })}
              >
                <SelectTrigger data-testid="add-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categoryOptions.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddTerm} disabled={isSubmitting} data-testid="add-term-submit">
              {isSubmitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              Add Term
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Term Dialog */}
      <Dialog open={!!editingTerm} onOpenChange={() => setEditingTerm(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-5 h-5" />
              Edit Term
            </DialogTitle>
            <DialogDescription>
              Update the terminology pair
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-1.5 block">English (EN-CA)</label>
              <Input
                value={formData.english}
                onChange={(e) => setFormData({ ...formData, english: e.target.value })}
                placeholder="Enter English term..."
                data-testid="edit-english-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">French (FR-CA)</label>
              <Input
                value={formData.french}
                onChange={(e) => setFormData({ ...formData, french: e.target.value })}
                placeholder="Enter French translation..."
                data-testid="edit-french-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Category</label>
              <Select
                value={formData.category}
                onValueChange={(value) => setFormData({ ...formData, category: value })}
              >
                <SelectTrigger data-testid="edit-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categoryOptions.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingTerm(null)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateTerm} disabled={isSubmitting} data-testid="edit-term-submit">
              {isSubmitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5" />
              Import Terminology from CSV
            </DialogTitle>
            <DialogDescription>
              Upload a CSV file with terminology pairs to bulk import
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="border-2 border-dashed border-gray-200 rounded-lg p-8 text-center">
              <Upload className="w-10 h-10 text-gray-400 mx-auto mb-4" />
              <p className="text-sm text-gray-600 mb-4">
                CSV must have <strong>english</strong> and <strong>french</strong> columns.<br />
                Optional: <strong>category</strong> column
              </p>
              <input
                type="file"
                accept=".csv"
                onChange={handleImportCSV}
                ref={fileInputRef}
                className="hidden"
                id="csv-upload"
              />
              <label htmlFor="csv-upload">
                <Button asChild disabled={isImporting}>
                  <span>
                    {isImporting ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Importing...</>
                    ) : (
                      <><Upload className="w-4 h-4 mr-2" /> Select CSV File</>
                    )}
                  </span>
                </Button>
              </label>
            </div>
            <div className="mt-4 p-3 bg-gray-50 rounded text-xs text-gray-500">
              <strong>Example CSV format:</strong>
              <pre className="mt-2 font-mono">
english,french,category
"snowfall warning","avertissement de neige",alerts
"wind chill","refroidissement éolien",temperature
              </pre>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportDialogOpen(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TerminologyPage;
